import React from "react"
import autobind from "class-autobind"
import {  IDate } from "../../../models"
import strings from "../../../localization/strings"
import { Drawer, Card, FontIcon } from "material-ui"
import AppStateStore from "../../../stores/AppStateStore"
import { green500, red500 } from "material-ui/styles/colors"
import moment from "moment"
import AccountStore from "../../../stores/AccountStore"
import * as utils from "../../../helpers/utils"
import { IScheduleBudgetInfo } from "../../../models/data/scheduling/ScheduleBudgetInfo"

interface IProps {
  bi: IScheduleBudgetInfo
  name: string
  startDate: IDate
  endDate: IDate
  open: boolean
  handleClose(): void
}

interface IState {
}

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export default class ScheduleBudgetCostSavingsDrawer extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)
    this.state = {
    }
    autobind(this)
  }

  
  showFillRateInfoDialog(){
    let fillRateDelta = this.props.bi.FillRateInfo.FillRate - 1

    if(fillRateDelta > 0){
      AppStateStore.setAlertDialogContent({
        content: `The current fill rate for this schedule is ${utils.toPercentage(this.props.bi.FillRateInfo.FillRate)}. Because the fill rate exceeds the optimal staff plan fill rate by ${utils.toPercentage(fillRateDelta)}, this schedule overpaid ${formatter.format(this.props.bi.SavingsPotential.FillRateSavings)} for work done above and beyond the staff plan based on the max census number during the schedule.`,
      })
    }else{
      AppStateStore.setAlertDialogContent({
        content: `The current fill rate for this schedule is ${utils.toPercentage(this.props.bi.FillRateInfo.FillRate)} which is below the target threshold of ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.TargetFillRate)}. This may lead to quality of care issues and worker dissatisfaction`,
      })
    }
  }

  showFTEUtilizationInfoDialog(){
    AppStateStore.setAlertDialogContent({
      content: "FTE utilization for the week of 02/27/2022 - 03/05/2022 is at 84%. 90% is considered the minimal optimal full time employee utilization number. 6% of the hours, which equals 124 hours, were worked by either contingent staff, float staff, or per diem staff. To increase cost savings, ensure tha tcore staff are working the agreed to weekly hours.",
    })
}

showCombinedFloatWorkTypeInfoDialog(){
  let combinedPercentage = utils.toPercentage(this.props.bi.WorkTypeInfo.Float + this.props.bi.WorkTypeInfo.PerDiem)
  AppStateStore.setAlertDialogContent({
    content: `There is currently a combined ${combinedPercentage} fill rate by float and per diem staff for this schedule. On average, float/per diem staff will cost ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.CombinedFloatPerDiemPremium)} more than core staff. Because the optimal float/per diem portion of hours is ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.CombinedFloatPerDiemTarget)}, yet this schedule had ${combinedPercentage} of float/per diem work, there is an overspend of ${formatter.format(this.props.bi.SavingsPotential.CombinedFloatPerDiemSavings)} over the target amount for this schedule.`,
  })
}


showFloatWorkTypeInfoDialog(){
  AppStateStore.setAlertDialogContent({
    content: `There is currently a ${utils.toPercentage(this.props.bi.WorkTypeInfo.Float)} fill rate by float staff for this schedule. On average, float staff will cost ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.FloatAveragePremium)} more than core staff. Because the optimal float portion of hours is ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.FloatTarget)}, yet this schedule had ${utils.toPercentage(this.props.bi.WorkTypeInfo.Float)} of float work, there is an overspend of ${formatter.format(this.props.bi.SavingsPotential.FloatSavings)} over the target amount for this schedule.`,
  })
}
showPerDiemWorkTypeInfoDialog(){
  AppStateStore.setAlertDialogContent({
    content: `There is currently a ${utils.toPercentage(this.props.bi.WorkTypeInfo.PerDiem)} fill rate by per diem staff for this schedule. On average, per diem staff will cost ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.PerDiemAveragePremium)} more than core staff. Because the optimal per diem portion of hours is ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.PerDiemTarget)}, yet this schedule had ${utils.toPercentage(this.props.bi.WorkTypeInfo.PerDiem)} of per diem work, there is an overspend of ${formatter.format(this.props.bi.SavingsPotential.PerDiemSavings)} over the target amount for this schedule.`,
  })
}

showAgencyWorkTypeInfoDialog(){
  AppStateStore.setAlertDialogContent({
    content: `There is currently a ${utils.toPercentage(this.props.bi.WorkTypeInfo.Agency)} fill rate by agency staff for this schedule. On average, agency staff will cost ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.AgencyAveragePremium)} more than core staff. Because the optimal agency portion of hours is ${utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.AgencyTarget)}, yet this schedule had ${utils.toPercentage(this.props.bi.WorkTypeInfo.Agency)} of agency work, there is an overspend of ${formatter.format(this.props.bi.SavingsPotential.AgencySavings)} over the target amount for this schedule.`,
  })
}

showAgencyFTESavingsBasedOnUtilizationDialog(){
  AppStateStore.setAlertDialogContent({
    content: `If Utilization rates can approach 100% for full time employees, there is an opportunity to remove ${this.props.bi.FTEUtilizationInfo.FTEEquivalentSavingsPotential} agency staff based on the currently selected criteria.`,
  })
}

showAgencyFTESavingsBasedOnFillRateDialog(){
  AppStateStore.setAlertDialogContent({
    content: `If fill rates can be lowered to 100% of the staff plan based on census numbers, there is an opportunity to remove ${this.props.bi.FillRateInfo.FTEEquivalentSavingsPotential} agency staff based on the currently selected criteria.`,
  })
}


showOvertimeOptimizationInfoDialog(){
  let otThresholdTarget = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.OvertimeTarget
  let workedOTPercentage = this.props.bi.OvertimeInfo.OvertimePercentageOfWhole
  let workedOTPercentageOverTarget = this.props.bi.OvertimeInfo.OvertimePercentageOfWhole - AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.OvertimeTarget
  let otPremium = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.OvertimePremium

  AppStateStore.setAlertDialogContent({
    content: `Overtime should not exceed ${utils.toPercentage(otThresholdTarget)} of all hours worked. Employees working this schedule have a combined total of ${utils.toPercentage(workedOTPercentage)} of their hours that were in overtime. This represents ${utils.toPercentage(workedOTPercentageOverTarget)} above the recommended rate. At a ${utils.toPercentage(otPremium)} premium paid for these hours, this could represent a signifant area of potential cost savings.`,
  })
}

getUtilizationHeader(){
  let wfSettings = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings
  let targetDelta = wfSettings.FTEUtilizationTarget - this.props.bi.FTEUtilizationInfo.Utilization;

  let color = targetDelta < 0 ? green500 : red500
  let arrow = targetDelta < 0 ? "fa fa-arrow-up" : "fa fa-arrow-down"

  return  (
      <h3>FTE Utilization: {Math.round(this.props.bi.FTEUtilizationInfo.Utilization * 100)}%  {this.props.bi.FTEUtilizationInfo.Utilization < .9 && <span style={{backgroundColor: color, color: "white", borderRadius: 20, padding: 5}}>
        {Math.round(targetDelta*100)}%  <FontIcon className={arrow} style={{ fontSize: 12, color: "white"}} /></span>}</h3>
    )
}


getUtilizationParagraph(){
  let wfSettings = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings
  let targetDelta = wfSettings.FTEUtilizationHeathlyThreshold - this.props.bi.FTEUtilizationInfo.Utilization;
  let healthy = targetDelta < 0
  let deltaFrom100 = 1 - this.props.bi.FTEUtilizationInfo.Utilization

  if(this.props.bi.FTEUtilizationInfo.Utilization > .90){
    return <p></p>
  }

  if(healthy){
    return (
      <p>
        While an FTE Utilization percentage of {utils.toPercentage(this.props.bi.FTEUtilizationInfo.Utilization)} is considered healthy, the target is {utils.toPercentage(wfSettings.FTEUtilizationTarget)} or higher FTE utilization. 
        This means that during the period in question, at least {utils.toPercentage(deltaFrom100)} of the hours worked were either overtime for existing core employees or premium labor such as float, per diem, or agency staff. 
        If the FTE utilization falls below the target, it suggests that there may be potential cost savings that can be achieved. In other words, reducing FTE underutilization is a key way to cut costs.
      </p>
    )
  }else{
    return (
      <p>
        An FTE Utilization percentage of {utils.toPercentage(this.props.bi.FTEUtilizationInfo.Utilization)} is considered unhealthy. 
        The target is {utils.toPercentage(wfSettings.FTEUtilizationTarget)} or higher FTE utilization with {utils.toPercentage(wfSettings.FTEUtilizationHeathlyThreshold)}  being considered healthy. 
        This means that during the period in question, at least {utils.toPercentage(deltaFrom100)} of the hours worked were either overtime for existing core employees or premium labor such as float, per diem, or agency staff. 
        If the FTE utilization falls below the target, it suggests that there may be potential cost savings that can be achieved. In other words, reducing FTE underutilization is a key way to cut costs.
      </p>
    )
  }
}

  render() {    
    let showFillRateInfo = (AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.TargetFillRate - this.props.bi.FillRateInfo.FillRate > 0) || this.props.bi.SavingsPotential.FillRateSavings > 0
    let combined = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.CombineFloatPerDiem
    let wfSettings = AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings

    return (
      <Drawer
        open={this.props.open}
        openSecondary
        width={1200}
        docked={false}
        overlayStyle={{ zIndex: 1500 }}
        containerStyle={{ zIndex: 1500, overflow: "scroll", opacity: open ? 1 : 0, transition: "opacity 200ms, transform 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms" }}
      >
          <div style={{ padding: 35, backgroundColor: AppStateStore.getDrawerHeaderColor(), textAlign: "center", color: "#ffffff", fontWeight: "normal", fontSize: 25 , marginBottom: 15}}>
              {strings.PotentialCostSavings}
              <i className="fa fa-close itemActionButton" style={{ float: "right" }} onClick={this.props.handleClose} />
          </div>
          <div style={{textAlign: "left", padding: 20}}>
            <h2>{this.props.name} <span style={{fontWeight: "normal"}}>{moment(this.props.startDate).format("MMMM Do, YYYY")}–{moment(this.props.endDate).format("MMMM Do, YYYY")} </span></h2>
            <Card style={{border: "solid 1px #efefef", padding: 20, width: "70%"}}>
              <div>
                {this.getUtilizationHeader()}
                <p style={{whiteSpace: "normal"}}>
                {this.getUtilizationParagraph()}                
                </p>
              </div>
            </Card>

            {false && 
              <Card style={{border: "solid 1px #efefef", padding: 20, width: "70%", marginTop: 20}}>
                {(this.props.bi.FTEUtilizationInfo.FTEEquivalentSavingsPotential > 0 || this.props.bi.FillRateInfo.FTEEquivalentSavingsPotential > 0) &&
                    <div><h3>Agency Full-Time Staff Reduction Potential: {(this.props.bi.FTEUtilizationInfo.FTEEquivalentSavingsPotential  + this.props.bi.FillRateInfo.FTEEquivalentSavingsPotential)}</h3></div>
                  }

                {this.props.bi.FTEUtilizationInfo.FTEEquivalentSavingsPotential > 0 && <div style={{marginLeft: 20}}><b>FTE Underutilization:</b> {this.props.bi.FTEUtilizationInfo.FTEEquivalentSavingsPotential} <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showAgencyFTESavingsBasedOnUtilizationDialog} /></div>}
                {this.props.bi.FillRateInfo.FTEEquivalentSavingsPotential > 0 && <div style={{marginLeft: 20}}><b>Over Staffing Based on overall Fill Rate:</b> {this.props.bi.FillRateInfo.FTEEquivalentSavingsPotential} <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showAgencyFTESavingsBasedOnFillRateDialog} /></div>}
              </Card>
            }

            <Card style={{border: "solid 1px #efefef", padding: 20, width: "70%", marginTop: 20}}>
              <div style={{display: "grid", gridTemplateColumns: "auto auto"}}>
                
                {showFillRateInfo && 
                  <div style={{gridRow: 1, gridColumn: 1}}>
                    <h4 style={{fontWeight: "normal"}}>Fill Rate Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showFillRateInfoDialog} /></h4>
                  </div>
                }
                {showFillRateInfo && 
                  <div style={{gridRow: 1, gridColumn: 2}}>
                    <h4>{this.props.bi.SavingsPotential.FillRateSavings > 0 ? 
                      formatter.format(this.props.bi.SavingsPotential.FillRateSavings) : 
                      <span>$0<sup>*</sup> <span style={{fontWeight: "normal"}}>{"(Below Optimal Fill Rate of " + utils.toPercentage(AccountStore.CurrentAccount.AccountSettings.WorkforceAssessmentSettings.TargetFillRate) + ")"}</span></span>
                      }</h4>
                  </div>     
                }

                {combined && this.props.bi.SavingsPotential.CombinedFloatPerDiemSavings > 0 && 
                  <div style={{gridRow: 2, gridColumn: 1}}>
                  <h4 style={{fontWeight: "normal"}}>Float/Per Diem Work Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showCombinedFloatWorkTypeInfoDialog} /></h4>
                  </div>
                }
                {combined && this.props.bi.SavingsPotential.CombinedFloatPerDiemSavings > 0 && 
                  <div style={{gridRow: 2, gridColumn: 2}}>
                    <h4><b>{formatter.format(this.props.bi.SavingsPotential.CombinedFloatPerDiemSavings)}</b></h4>
                  </div>     
                }

                {!combined && this.props.bi.SavingsPotential.FloatSavings > 0 && 
                  <div style={{gridRow: 2, gridColumn: 1}}>
                  <h4 style={{fontWeight: "normal"}}>Float Work Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showFloatWorkTypeInfoDialog} /></h4>
                  </div>
                }
                {!combined && this.props.bi.SavingsPotential.FloatSavings > 0 && 
                  <div style={{gridRow: 2, gridColumn: 2}}>
                    <h4><b>{formatter.format(this.props.bi.SavingsPotential.FloatSavings)}</b></h4>
                  </div>     
                }
                {!combined && this.props.bi.SavingsPotential.PerDiemSavings > 0 && 
                  <div style={{gridRow: 3, gridColumn: 1}}>
                  <h4 style={{fontWeight: "normal"}}>Per Diem Work Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showPerDiemWorkTypeInfoDialog} /></h4>
                  </div>
                }
                {!combined && this.props.bi.SavingsPotential.PerDiemSavings > 0 && 
                  <div style={{gridRow: 3, gridColumn: 2}}>
                    <h4><b>{formatter.format(this.props.bi.SavingsPotential.PerDiemSavings)}</b></h4>
                  </div>     
                }

                {this.props.bi.SavingsPotential.AgencySavings > 0 && 
                  <div style={{gridRow: 4, gridColumn: 1}}>
                  <h4 style={{fontWeight: "normal"}}>Agency Work Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showAgencyWorkTypeInfoDialog} /></h4>
                  </div>
                }
                {this.props.bi.SavingsPotential.AgencySavings > 0 && 
                  <div style={{gridRow: 4, gridColumn: 2}}>
                    <h4><b>{formatter.format(this.props.bi.SavingsPotential.AgencySavings)}</b></h4>
                  </div>     
                }


                {this.props.bi.SavingsPotential.OvertimeSavings > 0 && 
                  <div style={{gridRow: 5, gridColumn: 1}}>
                  <h4 style={{fontWeight: "normal"}}>Overtime Optimization Savings Potential: <i className="fa fa-info-circle" style={{cursor: "pointer"}} onClick={this.showOvertimeOptimizationInfoDialog} /></h4>
                  </div>
                }
                {this.props.bi.SavingsPotential.OvertimeSavings > 0 && 
                  <div style={{gridRow: 5, gridColumn: 2}}>
                    <h4><b>{formatter.format(this.props.bi.SavingsPotential.OvertimeSavings)}</b></h4>
                  </div>     
                }

                <div style={{gridRow: 6, gridColumn: 1}}>
                  <h2>Total Savings Potential: </h2>
                </div>
                <div style={{gridRow: 6, gridColumn: 2}}>
                  <h2><b>{formatter.format(this.props.bi.SavingsPotential.TotalSavings)}</b></h2>
                </div>  
              </div>
            </Card>
            {wfSettings && 
              <Card style={{border: "solid 1px #efefef", padding: 20, width: "70%", marginTop: 20}}>
                <h3>Assumptions</h3>
                <ul>
                  <li><b>Fill Rate Target:</b> {utils.toPercentage(wfSettings.TargetFillRate)}</li>
                  <li><b>Utilization Target:</b> {utils.toPercentage(wfSettings.FTEUtilizationTarget)}</li>
                  <li><b>Healthy Utilization Target:</b> {utils.toPercentage(wfSettings.FTEUtilizationHeathlyThreshold)}</li>
                  <li><b>Target Overtime:</b> {utils.toPercentage(wfSettings.OvertimeTarget)}</li>
                  <li><b>Target Float / Per Diem:</b> {utils.toPercentage(wfSettings.CombinedFloatPerDiemTarget)}</li>
                  <li><b>Target Agency:</b> {utils.toPercentage(wfSettings.AgencyTarget)}</li>
                  <li><b>Agency Average Premium:</b> {utils.toPercentage(wfSettings.AgencyAveragePremium)}</li>
                  <li><b>Float / Per Diem Average Premium:</b> {utils.toPercentage(wfSettings.CombinedFloatPerDiemPremium)}</li>
                </ul>
              </Card>
            }
          </div>
      </Drawer>
    )
  }
}